import { useCallback, useEffect, useRef, useState } from 'react';
import { apiUrl } from '../../config/api';

/**
 * Category list.
 *
 * Three components mount this hook on most pages (the header rail, the
 * landing grid and the filter panel), which used to mean three identical
 * requests per navigation. The in-flight promise and its result are cached
 * at module scope so they share one request; `refetch` bypasses the cache.
 */
let cache = null;
let inFlight = null;

const load = (force = false) => {
  if (!force && cache) return Promise.resolve(cache);
  if (!force && inFlight) return inFlight;

  inFlight = fetch(apiUrl('/api/category'), { method: 'GET' })
    .then((response) => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.json();
    })
    .then((data) => {
      cache = data.category || [];
      return cache;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

const useCategories = () => {
  const [categories, setCategories] = useState(() => cache || []);
  const [loading, setLoading] = useState(() => !cache);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchCategories = useCallback(async (force = false) => {
    if (force) setLoading(true);
    try {
      const result = await load(force);
      if (!isMounted.current) return;
      setCategories(result);
      setError(null);
    } catch (err) {
      if (isMounted.current) setError(err.message);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (cache) return;
    fetchCategories();
  }, [fetchCategories]);

  return { categories, loading, error, fetchCategories };
};

export default useCategories;
